import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

// ─────────────────────────────────────────────
// VAPID PUBLIC KEY
// Ersetze mit deinem eigenen VAPID Public Key.
// Generieren: npx web-push generate-vapid-keys
// Den Public Key hier eintragen, den Private Key
// als Secret in Supabase (VAPID_PRIVATE_KEY).
// ─────────────────────────────────────────────
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function getUserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return "Europe/Berlin"; }
}

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────
export function usePushNotifications(userId) {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [supported, setSupported]       = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (ok) setPermission(Notification.permission);
  }, []);

  // Bestehende Subscription laden
  useEffect(() => {
    if (!userId || !supported) return;
    loadExistingSubscription();
  }, [userId, supported]);

  async function loadExistingSubscription() {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) setSubscription(sub);
    } catch {}
  }

  // ── Permission anfragen + Subscription erstellen ──
  const requestPermission = useCallback(async (settings = {}) => {
    if (!supported) throw new Error("Push-Notifications werden in diesem Browser nicht unterstützt.");
    if (!VAPID_PUBLIC_KEY) throw new Error("VAPID Public Key fehlt. Bitte .env.local prüfen.");

    setLoading(true);
    try {
      // 1. Permission anfragen
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") throw new Error("Berechtigung verweigert.");

      // 2. Service Worker registrieren
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // 3. Push Subscription erstellen
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      setSubscription(sub);

      // 4. In Supabase speichern
      await saveSubscription(sub, settings);
      return true;
    } finally {
      setLoading(false);
    }
  }, [supported, userId]);

  // ── Einstellungen aktualisieren ──────────────
  const updateSettings = useCallback(async (settings) => {
    if (!subscription || !userId) return;
    const key = subscription.toJSON();
    await supabase
      .from("push_subscriptions")
      .update({
        morning_enabled: settings.morningEnabled ?? true,
        morning_time:    settings.morningTime    ?? "07:00",
        night_enabled:   settings.nightEnabled   ?? true,
        night_time:      settings.nightTime      ?? "21:00",
      })
      .eq("user_id", userId)
      .eq("endpoint", key.endpoint);
  }, [subscription, userId]);

  // ── Subscription deaktivieren ────────────────
  const unsubscribe = useCallback(async () => {
    if (!subscription || !userId) return;
    setLoading(true);
    try {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await supabase
        .from("push_subscriptions")
        .delete()
        .eq("user_id", userId)
        .eq("endpoint", endpoint);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [subscription, userId]);

  // ── Intern: Subscription speichern ──────────
  async function saveSubscription(sub, settings = {}) {
    const key = sub.toJSON();
    await supabase
      .from("push_subscriptions")
      .upsert({
        user_id:         userId,
        endpoint:        key.endpoint,
        p256dh:          key.keys?.p256dh || "",
        auth:            key.keys?.auth   || "",
        morning_enabled: settings.morningEnabled ?? true,
        morning_time:    settings.morningTime    ?? "07:00",
        night_enabled:   settings.nightEnabled   ?? true,
        night_time:      settings.nightTime      ?? "21:00",
        timezone:        getUserTimezone(),
      }, { onConflict: "user_id,endpoint" });
  }

  return {
    supported,
    permission,
    subscription,
    loading,
    isActive: !!subscription && permission === "granted",
    requestPermission,
    updateSettings,
    unsubscribe,
  };
}